import {
  Table,
  Paging,
  EllipsisSpan,
  usePaging,
  Form,
  Input,
  CustomFilter,
  Modal,
  Select,
  message,
} from '@maxtropy/components';
import { FC, useState, Key, useEffect } from 'react';
import { ColumnType } from 'antd/es/table';
import { CpStaffPageReq, getCpOrgInfoList, getCpStaffPageList, StaffListResponse } from '../../../../api/customer';
import { useRequest } from 'ahooks';
import { LeftNavPreference } from '../../../../api/const';

export const userColumns: ColumnType<StaffListResponse>[] = [
  {
    title: '姓名',
    dataIndex: 'name',
    width: 200,
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '工号',
    dataIndex: 'staffCode',
    width: 200,
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '手机号',
    width: 100,
    dataIndex: 'cellphone',
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '所属组织',
    width: 300,
    dataIndex: 'orgName',
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

interface UserModalProps {
  selectedData: StaffListResponse[];
  setSelectedData: (value: StaffListResponse[]) => void;
  selectedMcid?: string;
  setSelectedMcid: (value: string) => void;
  onCancel: () => void;
}

interface SearchParams extends Omit<CpStaffPageReq, 'size' | 'page' | 'type' | 'mcids'> {
  mcids?: string;
}

const UserModal: FC<UserModalProps> = ({ selectedData, setSelectedData, setSelectedMcid, selectedMcid, onCancel }) => {
  const pagingInfo = usePaging(100);
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;

  const [searchParams, setSearchParams] = useState<SearchParams>({ mcids: selectedMcid });
  const [loading, setLoading] = useState<boolean>(false);

  const [searchForm] = Form.useForm();

  const [data, setData] = useState<StaffListResponse[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<StaffListResponse[]>([]);

  // 获取外部组织
  const { data: cpOrgList } = useRequest(getCpOrgInfoList);

  useEffect(() => {
    searchForm.setFieldsValue({
      mcids: selectedMcid,
    });
  }, [selectedMcid]);

  useEffect(() => {
    setLoading(true);
    getCpStaffPageList({
      page: pageOffset,
      size: pageSize,
      ...searchParams,
      mcids: searchParams?.mcids ? [searchParams.mcids] : [],
    })
      .then(res => {
        setData(res.list);
        setTotalCount(res.total);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [pageOffset, pageSize, searchParams]);

  useEffect(() => {
    if (selectedData.length > 0) {
      setSelectedRowKeys(selectedData.map(record => `${record.muid}-${record.orgId}`));
      setSelectedRows(selectedData);
    }
  }, []);

  const onOk = () => {
    if (selectedRows.length > 0) {
      const isUnique = new Set(selectedRows.map(i => i.orgId)).size === 1;
      if (!isUnique) {
        message.warning('不能选择多个组织');
        return;
      }
      // 如果selectedRows中包含selectedData的数据，则使用selectedData中的数据替换
      // 此操作是为了保留导航栏状态字段
      const newData = selectedRows.map(item => {
        const findData = selectedData.find(i => i.muid === item.muid);
        return findData || { ...item, navigationPreference: LeftNavPreference.OPEN_CLOSE };
      });
      setSelectedData(newData);
      setSelectedMcid(selectedRows[0].orgId);
      onCancel();
    } else {
      Modal.warning({
        title: '请先选择用户',
      });
    }
  };

  const rowSelection = {
    onChange: (selectedRowKeys: Key[], selectedRows: StaffListResponse[]) => {
      const isUnique = selectedRows.length === 0 || new Set(selectedRows.map(i => i.orgId)).size === 1;

      if (!isUnique) {
        message.warning('不能选择多个组织');
        return;
      }
      if (selectedRows.length >= 100) {
        message.warning('最多可选择100个用户');
        return;
      }
      setSelectedRowKeys(selectedRows.map(record => `${record.muid}-${record.orgId}`));
      setSelectedRows(selectedRows);
    },
    preserveSelectedRowKeys: true,
    selectedRowKeys: selectedRowKeys,
  };

  const onFinish = (record: SearchParams) => {
    setPageOffset(1);
    setSearchParams(record);
  };

  const onReset = () => {
    setPageOffset(1);
    setSearchParams({ mcids: selectedMcid });
  };

  return (
    <Modal title="用户信息" open={true} size="big" onOk={onOk} onCancel={onCancel} destroyOnClose>
      <CustomFilter<SearchParams>
        form={searchForm}
        onFinish={onFinish}
        onReset={onReset}
        colSpan={8}
        initialValues={{ mcids: selectedMcid }}
      >
        <Form.Item name="name" label="姓名">
          <Input placeholder="请输入" maxLength={20} />
        </Form.Item>
        <Form.Item name="cellphone" label="手机号">
          <Input placeholder="请输入" maxLength={20} />
        </Form.Item>
        <Form.Item name="mcids" label="所在组织">
          <Select
            style={{ width: '100%' }}
            placeholder="请选择组织"
            showSearch
            optionFilterProp="label"
            disabled={!!selectedMcid}
            options={(cpOrgList?.list ?? []).map(item => ({ label: item.orgName, value: item.orgId }))}
          />
        </Form.Item>
        <Form.Item name="staffCode" label="工号">
          <Input placeholder="请输入" />
        </Form.Item>
      </CustomFilter>
      <Table
        loading={loading}
        rowSelection={{
          type: 'checkbox',
          ...rowSelection,
        }}
        sticky
        scroll={{ y: 300 }}
        rowKey={record => `${record.muid}-${record.orgId}`}
        columns={userColumns}
        dataSource={data}
        showSelectedCount
        selectedCount={selectedRows.length}
      >
        <Paging pagingInfo={pagingInfo} />
      </Table>
    </Modal>
  );
};

export default UserModal;
